import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useDeactivateAccount } from '../../data/useInternalAdmin';

interface DeactivateAccountDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string | undefined;
  accountName: string;
  isDeactivated: boolean;
}

const DeactivateAccountDialog: React.FC<DeactivateAccountDialogProps> = ({
  open,
  onClose,
  accountId,
  accountName,
  isDeactivated,
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const deactivate = useDeactivateAccount();

  const action = isDeactivated ? 'reactivate' : 'deactivate';

  const handleConfirm = () => {
    if (!accountId) return;
    setError(null);
    deactivate.mutate(
      { accountId, action },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (err: any) => {
          const detail =
            err?.response?.data?.detail || `Failed to ${action} account`;
          setError(detail);
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isDeactivated ? 'Reactivate Account' : 'Deactivate Account'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText>
          {isDeactivated ? (
            <>
              Reactivate <strong>{accountName}</strong>? Users will regain
              access to this account.
            </>
          ) : (
            <>
              Deactivate <strong>{accountName}</strong>? All users will lose
              access immediately and all active assumed roles will be ended.
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          color={isDeactivated ? 'primary' : 'error'}
          variant="contained"
          disabled={deactivate.isPending}
        >
          {deactivate.isPending
            ? isDeactivated
              ? 'Reactivating...'
              : 'Deactivating...'
            : isDeactivated
              ? 'Reactivate'
              : 'Deactivate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeactivateAccountDialog;
